import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageLayout } from '../components/layout';
import { Loader2, RefreshCw, Copy, Check, X, Users, FileText, MessageSquare, Share2, User, Tag, Video, Image, Plus, Sparkles, Info, Save, CheckCircle, Brain, ImageOff } from 'lucide-react';
import { BrandDropdown } from '../components/common/BrandDropdown';
import { ContentToneSelect } from '../components/common/ContentToneSelect';
import { LanguageInput } from '../components/common/LanguageInput';
import { LanguageDropdown } from '../components/common/LanguageDropdown';
import { SocialMediaDropdown } from '../components/common/SocialMediaDropdown';
import { TagInput } from '../components/common/TagInput';
import { ContentResultCard } from '../components/content/ContentResultCard';
import { useBrands } from '../hooks/useBrands';
import { usePersonas } from '../hooks/usePersonas';
import { useContentGeneration } from '../hooks/useContentGeneration';
import { useContentIdeasWithAccumulation } from '../hooks/useContentIdeas';
import { CONTENT_LENGTH_OPTIONS, SOCIAL_PLATFORMS } from '../config/constants';
import { ContentGenerationRequest, ContentGenerationResponse, PlatformContent } from '../services/contentService';
import type { SocialPlatform } from '../components/icons/SocialIcon';
import { SocialIcon } from '../components/icons/SocialIcon';
import { Link } from 'react-router-dom';
import { ImageStyleSelect } from '../components/common/ImageStyleSelect';
import { ContentGenerationLoader } from '../components/content/ContentGenerationLoader';
import { ImageSizeSelect } from '../components/common/ImageSizeSelect';
import { PersonasResponse } from '../types/personas';
import { saveDraft } from '../services/draftService';
import { AIPostDraftModal } from '../components/modals/AIPostDraftModal';
import { WaitingModal } from '../components/common/WaitingModal';
import { AISimpleGenerationModal } from '../components/modals/AISimpleGenerationModal';
import { useImageRegeneration } from '../hooks/useImageRegeneration';
import { 
  convertImageSizeToAspectRatio, 
  convertImageStyleToStyleType 
} from '../services/imageService';
import type { ImageSize } from '../components/common/ImageSizeSelect';
import { SocialProfilePicture } from '../components/common/SocialProfilePicture';
import { logger } from '../utils/logger';

// Define content idea interface to match the API response
interface ContentIdea {
  title: string;
  description: string;
  platforms: string[];
  valueProposition: string;
  contentFormat: string;
}

// Define platform type for SocialMediaDropdown
interface SocialMediaPlatform {
  id: SocialPlatform | 'all';
  name: string;
}

// Update ContentGenerationRequest to include imageStyle
interface ContentGenerationRequestWithStyle extends ContentGenerationRequest {
  imageStyle?: string;
  imageSize?: string;
  style_type?: string;
  aspect_ratio?: string;
}

// Memoize the ImageStyleSelect component to prevent unnecessary rerenders
const MemoizedImageStyleSelect = React.memo(({ value, onChange }: { value: string, onChange: (value: string) => void }) => {
  // Force refresh on language change
  const { i18n } = useTranslation();
  const [key, setKey] = useState(0);
  
  // Reset key when language changes to force a full remount
  React.useEffect(() => {
    setKey(prev => prev + 1);
  }, [i18n.language]);
  
  return (
    <ImageStyleSelect 
      key={`image-style-${key}-${i18n.language}`} 
      value={value} 
      onChange={onChange} 
    />
  );
});

// Memoize the ContentToneSelect component to prevent unnecessary rerenders
const MemoizedContentToneSelect = React.memo(({ value, onChange }: { value: string, onChange: (value: string) => void }) => {
  // Force refresh on language change
  const { i18n } = useTranslation();
  const [key, setKey] = useState(0);
  
  // Reset key when language changes to force a full remount
  React.useEffect(() => {
    setKey(prev => prev + 1);
  }, [i18n.language]);
  
  return (
    <ContentToneSelect 
      key={`tone-select-${key}-${i18n.language}`} 
      value={value} 
      onChange={onChange} 
    />
  );
});

// Memoize the ImageSizeSelect component to prevent unnecessary rerenders
const MemoizedImageSizeSelect = React.memo(({ value, onChange }: { value: any, onChange: (value: any) => void }) => {
  // Force refresh on language change
  const { i18n } = useTranslation();
  const [key, setKey] = useState(0);
  
  // Reset key when language changes to force a full remount
  React.useEffect(() => {
    setKey(prev => prev + 1);
  }, [i18n.language]);
  
  return (
    <ImageSizeSelect 
      key={`image-size-${key}-${i18n.language}`} 
      value={value} 
      onChange={onChange} 
    />
  );
});

// Memoize the DescriptionTextarea component to prevent unnecessary rerenders
const MemoizedDescriptionTextarea = React.memo(({ 
  value, 
  onChange, 
  placeholder 
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder: string;
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  
  // Add event listeners to capture all possible ways of editing content
  React.useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    // Mark as edited on keydown, paste, and cut events
    const handleEdit = () => {
      // We'll keep this functionality in the parent component's onChange handler
      // to avoid having duplicated state updates
    };
    
    textarea.addEventListener('paste', handleEdit);
    textarea.addEventListener('cut', handleEdit);
    textarea.addEventListener('drop', handleEdit); // For drag and drop edits
    
    return () => {
      textarea.removeEventListener('paste', handleEdit);
      textarea.removeEventListener('cut', handleEdit);
      textarea.removeEventListener('drop', handleEdit);
    };
  }, []);
  
  return (
    <textarea
      id="shortDescription"
      name="shortDescription"
      rows={3}
      ref={textareaRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
    />
  );
});

const ContentCreation = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Ref to control when content generation is allowed
  const allowContentGeneration = React.useRef(false);
  // Ref to track if the description field has been manually edited
  const manuallyEditedDescription = React.useRef(false);
  // Ref to track if we've already done an automatic content idea selection
  const hasAutoSelectedIdea = React.useRef(false);
  
  // New state for AIPostDraftModal
  const [isAIPostDraftModalOpen, setIsAIPostDraftModalOpen] = useState(false);
  const [selectedPostForEditing, setSelectedPostForEditing] = useState<{
    platform: string;
    content: string;
    hashtags?: string[];
    imageUrl?: string | null;
  } | null>(null);
  const [isSavingDrafts, setIsSavingDrafts] = useState(false);
  
  // Image regeneration modal state
  const [isImageRegenerationModalOpen, setIsImageRegenerationModalOpen] = useState(false);
  const [temporaryGeneratedImageUrl, setTemporaryGeneratedImageUrl] = useState<string | null>(null);
  
  // Keywords input state
  const [keywordsInputValue, setKeywordsInputValue] = useState('');
  
  // Image regeneration hook
  const imageRegeneration = useImageRegeneration();
  
  useEffect(() => {
    document.title = t('contentCreation.title', 'Content Creation');
  }, [t]);
  
  const [formData, setFormData] = useState<ContentGenerationRequestWithStyle>({
    brandId: '',
    personaId: '',
    platforms: [] as string[],
    topic: '',
    shortDescription: '',
    tone: 'professional',
    contentLength: 'medium',
    language: (() => {
      const rawLanguage = (typeof i18n.language === 'string' ? i18n.language : 'en') as string;
      const availableLanguages = [
        'en', 'en-US', 'en-GB', 'fr', 'fr-CA', 'de', 'es', 'es-MX', 
        'it', 'pt', 'pt-BR', 'nl', 'ru', 'ar', 'zh', 'ja', 'ko', 'hi', 'bn', 
        'tr', 'vi', 'tl', 'fa', 'pl', 'th', 'sv', 'ro', 'uk', 'cs', 'lb', 'da', 'fi', 'no'
      ];
      
      // Convert to lowercase for case-insensitive matching
      const normalizedLanguage = rawLanguage.toLowerCase();
      
      // Try exact match first, then language-only fallback
      if (availableLanguages.includes(normalizedLanguage)) {
        return normalizedLanguage;
      }
      const languageOnly = normalizedLanguage.split('-')[0];
      return availableLanguages.includes(languageOnly) ? languageOnly : 'en';
    })(),
    keywords: [] as string[],
    sourceUrl: '',
    format: 'regular',
    generateImage: false,
    imageStyle: 'auto',
    imageSize: '4:5',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generatedContent, setGeneratedContent] = useState<ContentGenerationResponse | null>(null);
  const [allCopied, setAllCopied] = useState(false);
  const [hasGeneratedNewContent, setHasGeneratedNewContent] = useState(false);

    // Get current language
  const currentLanguage = i18n.language || 'en';

  // Convert social platforms to the format expected by SocialMediaDropdown
  const socialPlatforms: SocialMediaPlatform[] = SOCIAL_PLATFORMS.map(platform => ({
    id: platform.value as SocialPlatform,
    name: platform.label
  }));

  // Fetch brands and personas
  const { data: brandsData } = useBrands();
  const brands = brandsData ? (brandsData as any).brands || [] : [];

  // Get connected social accounts for the selected brand
  const getConnectedSocialAccounts = () => {
    if (!formData.brandId || !brands.length) return [];
    
    const selectedBrand = brands.find((b: any) => b.id === formData.brandId);
    if (!selectedBrand?.social_accounts) return [];
    
    return selectedBrand.social_accounts
      .filter((account: any) => account.platform && account.username)
      .map((account: any) => ({
        platform: account.platform.toLowerCase() as SocialPlatform,
        username: account.username,
        picture_url: account.profile_url || account.picture_url || ''
      }));
  };

  const connectedAccounts = getConnectedSocialAccounts();
  


  // Auto-select brand if there's only one
  useEffect(() => {
    if (brands && brands.length === 1 && !formData.brandId) {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Auto-selecting single brand', brands[0].id);
      }
      // console.log('Auto-selecting single brand:', brands[0].id);
      setFormData(prev => ({ ...prev, brandId: brands[0].id }));
    }
  }, [brands, formData.brandId]);

  // Handle navigation from BrandCard or PersonaCard
  useEffect(() => {
    const state = location.state as any;
    if (state) {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Received navigation state', state);
      }

      const { 
        selectedBrandId, 
        selectedPersonaId, 
        createManually, 
        returnToBrands, 
        returnToPersonas, 
        recommendedPlatforms 
      } = state;
      
      // Set brand if provided
      if (selectedBrandId && selectedBrandId !== formData.brandId) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Setting brand from navigation', selectedBrandId);
        }
        // console.log('Setting brand from navigation:', selectedBrandId);
        setFormData(prev => ({ ...prev, brandId: selectedBrandId }));
      }
      
      // Set persona if provided (will be set after personas are loaded)
      if (selectedPersonaId && selectedPersonaId !== formData.personaId) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Will set persona from navigation', selectedPersonaId);
        }
        console.log('Will set persona from navigation:', selectedPersonaId);
        // We'll set this in a separate effect after personas are loaded
      }
      
      // Set recommended platforms if provided from PersonaCard
      if (recommendedPlatforms && Array.isArray(recommendedPlatforms) && recommendedPlatforms.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Setting recommended platforms', recommendedPlatforms);
        }
        console.log('ContentCreation: Setting recommended platforms from persona:', recommendedPlatforms);
        // Normalize platform names to match our expected format
        const availablePlatformIds = SOCIAL_PLATFORMS.map(p => p.value);
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Available platform IDs', availablePlatformIds);
        }
        console.log('ContentCreation: Available platform IDs:', availablePlatformIds);
        
        const normalizedPlatforms = recommendedPlatforms
          .map(platform => platform.toLowerCase())
          .filter(platform => availablePlatformIds.includes(platform));
        
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Normalized platforms', normalizedPlatforms);
          logger.debug('ContentCreation', 'Current platforms before update', formData.platforms);
        }
        console.log('ContentCreation: Normalized platforms to set:', normalizedPlatforms);
        
        if (normalizedPlatforms.length > 0) {
          setFormData(prev => {
            if (process.env.NODE_ENV === 'development') {
              logger.debug('ContentCreation', 'Updating platforms', { from: prev.platforms, to: normalizedPlatforms });
            }
            console.log('ContentCreation: Updating platforms from', prev.platforms, 'to', normalizedPlatforms);
            return { ...prev, platforms: normalizedPlatforms };
          });
        } else {
          if (process.env.NODE_ENV === 'development') {
            logger.debug('ContentCreation', 'No valid platforms to set');
          }
          console.log('ContentCreation: No valid platforms found to set');
        }
      }
      
      // Clear the state after handling to prevent re-processing
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  // Only use mock data if explicitly debugging, otherwise use real API data
  // Load ALL personas once and filter client-side (same approach as Personas.tsx)
  const { data: personasData, isLoading: isLoadingPersonas } = usePersonas(
    undefined, // No server-side filtering by brand - we'll filter client-side  
    false, 
    false
  );
  const allPersonas = (personasData as PersonasResponse)?.personas || [];
  
  // Filter personas by selected brand (client-side filtering)
  const personas = useMemo(() => {
    if (!allPersonas.length) return [];
    
    // If no brand is selected, show all personas (default behavior)
    if (!formData.brandId) return allPersonas;
    
    // If a brand is selected, filter by that brand
    return allPersonas.filter(persona => persona.brandId === formData.brandId);
  }, [allPersonas, formData.brandId]);

  // Compute the effective language for content ideas (after personas are available)
  const effectiveLanguage = useMemo(() => {
    // If we have a selected persona, use its preferred language
    if (formData.personaId && personas.length > 0) {
      const selectedPersona = personas.find(p => p.id === formData.personaId);
      if (selectedPersona && Array.isArray(selectedPersona.languages) && selectedPersona.languages.length > 0) {
        return selectedPersona.languages[0];
      }
    }
    // Otherwise use form language
    return formData.language;
  }, [formData.personaId, personas, formData.language]);

    // Fetch content ideas with accumulation and exclusion logic
  const { 
    ideas: contentIdeas,
    isLoading: isIdeasLoading, 
    isGeneratingMore: isGeneratingMoreIdeas,
    error: ideasError,
    generateMoreIdeas,
    refetch: refetchIdeas
  } = useContentIdeasWithAccumulation({
    brandId: formData.brandId || '',
    personaId: formData.personaId || '',
    appLanguage: effectiveLanguage || 'en' // Use computed language instead of form language
  });

  // Handle error messages
  const ideasErrorMessage = (ideasError as Error)?.message;
  const hasIdeasError = Boolean(ideasError);
  
  // Reset auto-selection flag when brand or persona changes
  useEffect(() => {
    if (formData.brandId && formData.personaId) {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Brand/Persona changed', `${formData.brandId}/${formData.personaId}`);
      }
      console.log(`🔄 Brand/Persona changed: ${formData.brandId}/${formData.personaId} - resetting auto-selection flag`);
      // Reset auto-selection flag when brand/persona changes to allow new auto-selection
      hasAutoSelectedIdea.current = false;
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Auto-selection flag reset');
      }
      console.log('✅ Auto-selection flag reset, new ideas will be auto-selected when they load');
    }
  }, [formData.brandId, formData.personaId]);

  // Auto-select a random content idea if user hasn't filled Source URL or Description (only once)
  useEffect(() => {
    // Only proceed if we have ideas, no manual input from user, and haven't auto-selected before
    if (contentIdeas.length > 0 && 
        !formData.sourceUrl?.trim() && 
        !formData.shortDescription?.trim() &&
        !manuallyEditedDescription.current &&
        !hasAutoSelectedIdea.current) {
      
      // Select a random idea
      const randomIndex = Math.floor(Math.random() * contentIdeas.length);
      const randomIdea = contentIdeas[randomIndex];
      
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Auto-selecting random content idea', randomIdea);
      }
      console.log('Auto-selecting random content idea (first time only):', randomIdea);
      
      // Mark that we've done an auto-selection
      hasAutoSelectedIdea.current = true;
      
      // Use the existing handleIdeaSelect function to maintain consistency
      handleIdeaSelect(randomIdea);
    }
  }, [contentIdeas, formData.sourceUrl, formData.shortDescription]);
  
  // Handle persona selection from navigation after personas are loaded
  useEffect(() => {
    const state = location.state as any;
    if (state?.selectedPersonaId && personas.length > 0) {
      const targetPersona = personas.find(p => p.id === state.selectedPersonaId);
      if (targetPersona && targetPersona.id !== formData.personaId) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Setting persona from navigation', targetPersona.id);
        }
        console.log('Setting persona from navigation:', targetPersona.id);
        setFormData(prev => ({ ...prev, personaId: targetPersona.id }));
      }
    }
  }, [personas, location.state, formData.personaId]);

  
  // Log for debugging
  useEffect(() => {
    if (formData.brandId) {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Current brand ID', formData.brandId);
        logger.debug('ContentCreation', 'Personas loaded', personas);
      }
      // Removed verbose console logs for current brand and personas
    }
  }, [formData.brandId, personas]);



  // Auto-select persona when brand changes
  useEffect(() => {
    if (formData.brandId && personas.length > 0) {
      // 1. Est-ce qu'il y a un seul persona ?
      if (personas.length === 1) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Auto-selecting single persona', personas[0].id);
        }
        // 2. Oui, alors on le sélectionne automatiquement
        console.log('Auto-selecting single persona:', personas[0].id);
        setFormData(prev => ({ ...prev, personaId: personas[0].id }));
      } else {
        // Non? On attend que l'utilisateur le fasse - on reset le persona si plusieurs disponibles
        if (process.env.NODE_ENV === 'development') {
          logger.debug('ContentCreation', 'Multiple personas available, waiting for user selection');
        }
        console.log('Multiple personas available, user needs to select');
        if (formData.personaId) {
          // Vérifier si le persona actuel est encore valide pour cette marque
          const isCurrentPersonaValid = personas.some(p => p.id === formData.personaId);
          if (!isCurrentPersonaValid) {
            setFormData(prev => ({ ...prev, personaId: '' }));
          }
        }
      }
    } else if (formData.brandId && personas.length === 0) {
      // Pas encore de personas chargés ou aucun persona pour cette marque
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'No personas available for this brand');
      }
      console.log('No personas available for this brand yet');
      setFormData(prev => ({ ...prev, personaId: '' }));
    }
  }, [formData.brandId, personas]);

  // Reset platforms when brand changes (no auto-selection)
  // But don't reset if platforms were just set from navigation
  useEffect(() => {
    if (formData.brandId && brands.length > 0 && !location.state) {
      // Only reset platforms if we're not coming from navigation
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ContentCreation', 'Brand changed, resetting platform selection');
      }
      console.log('Brand changed, resetting platform selection');
      setFormData(prev => ({ ...prev, platforms: [] }));
    }
  }, [formData.brandId, brands, location.state]);

  // Content generation mutation
  const contentGeneration = useContentGeneration();

  // Create a ref for the results section
  const resultsRef = useRef<HTMLDivElement>(null);

  // Function to scroll to the top of the page
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to scroll to the results section
  const scrollToResults = () => {
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Function to transform the new API response format to the format expected by the UI
  const transformContentResponse = (response: ContentGenerationResponse) => {
    if (process.env.NODE_ENV === 'development') {
      logger.debug('ContentCreation', 'Transforming content response', response);
    }
    console.log('Transforming content response:', response);
    
    // Handle completely empty responses
    if (!response) {
      console.error('Content response is empty or undefined');
      return {
        success: false,
        message: 'Empty response received',
        data: { contents: [] }
      };
    }
    
    // Extract imageUrl from the response (main image URL at the top level)
    const imageUrl = response.imageUrl;
    
    // Check if we have the new API format with generatedContents array
    if (response.generatedContents && Array.isArray(response.generatedContents)) {
      try {
        console.log('Processing response with generatedContents array');
        
        const transformedContents = response.generatedContents.map(item => {
          // Process content to convert escaped newlines to real ones
          let processedContent = item.generatedContent || '';
          if (typeof processedContent === 'string' && processedContent.includes('\\n')) {
            processedContent = processedContent.replace(/\\n/g, '\n');
          }
          
          return {
            platform: item.platform?.toLowerCase() || 'unknown',
            content: processedContent,
            hashtags: extractHashtags(processedContent),
            // Use platform-specific image if available, otherwise fall back to main image
            imageUrl: item.imageUrl || response.imageUrl
          };
        });
        
        console.log('Transformed content from generatedContents:', transformedContents);
        
        return {
          ...response,
          success: true,
          data: { contents: transformedContents }
        };
      } catch (error) {
        console.error('Error transforming generatedContents:', error);
        // Continue to fallback handler
      }
    }
    
    // Try to extract any content we can find in the response
    let fallbackContents: Array<{platform: string; content: string | PlatformContent; hashtags?: string[]}> = [];
    
    try {
      if (response.generatedContents && Array.isArray(response.generatedContents)) {
        fallbackContents = response.generatedContents.map(content => {
          // Process content to convert escaped newlines to real ones
          let processedContent = content.generatedContent || '';
          if (typeof processedContent === 'string' && processedContent.includes('\\n')) {
            processedContent = processedContent.replace(/\\n/g, '\n');
          }
          
          return {
            platform: content.platform?.toLowerCase() || 'unknown',
            content: processedContent,
            hashtags: extractHashtags(processedContent)
          };
        });
        console.log('Extracted from generatedContents array:', fallbackContents);
      } else if (response.data?.contents) {
        // Process any string content to fix escaped newlines
        fallbackContents = response.data.contents.map(item => {
          if (typeof item.content === 'string' && item.content.includes('\\n')) {
            return {
              ...item,
              content: item.content.replace(/\\n/g, '\n')
            };
          }
          return item;
        });
        console.log('Using existing contents array:', fallbackContents);
      } else if (response.content?.platforms) {
        // Handle the 'content.platforms' object format
        fallbackContents = Object.entries(response.content.platforms).map(([platform, content]) => {
          // Handle string content with escaped newlines
          if (typeof content === 'string') {
            const stringContent = content as string;
            if (stringContent.includes('\\n')) {
              return {
                platform: platform.toLowerCase(),
                content: stringContent.replace(/\\n/g, '\n'),
                hashtags: extractHashtags(stringContent.replace(/\\n/g, '\n'))
              };
            }
            return {
              platform: platform.toLowerCase(),
              content: stringContent,
              hashtags: extractHashtags(stringContent)
            };
          } else if (content && typeof content === 'object') {
            const objContent = content as Record<string, any>;
            if ('text' in objContent && typeof objContent.text === 'string' && objContent.text.includes('\\n')) {
              const processedContent = {
                ...objContent,
                text: objContent.text.replace(/\\n/g, '\n')
              };
              return {
                platform: platform.toLowerCase(),
                content: processedContent,
                hashtags: extractHashtags(processedContent)
              };
            }
          }
          
          // Default case - return as is
          return {
            platform: platform.toLowerCase(),
            content: content,
            hashtags: extractHashtags(content)
          };
        });
        console.log('Extracted from content.platforms object:', fallbackContents);
      }
    } catch (error) {
      console.error('Error extracting content from response:', error);
    }
    
    return {
      ...response,
      success: response.success === undefined ? true : response.success, // Preserve success value if it exists
      message: response.message || 'Content format not recognized',
      data: { 
        contents: fallbackContents.length > 0 ? fallbackContents : []
      }
    };
  };
  
  // Helper function to extract hashtags from content
  const extractHashtags = (content: string | PlatformContent | any) => {
    const hashtags: string[] = [];
    
    // Ensure we have a string to extract hashtags from
    let contentString = '';
    
    if (typeof content === 'string') {
      // Preserve the original string with newlines
      contentString = content;
    } else if (content && typeof content === 'object') {
      // Check if it has a text property (PlatformContent)
      if ('text' in content && content.text) {
        contentString = typeof content.text === 'string' 
          ? content.text 
          : JSON.stringify(content.text);
      } else {
        // Otherwise stringify the whole object
        contentString = JSON.stringify(content);
      }
    } else if (content) {
      contentString = String(content);
    }
    
    // Extract hashtags from the content string without modifying newlines
    const regex = /#(\w+)/g;
    let match;
    
    while ((match = regex.exec(contentString)) !== null) {
      hashtags.push(`#${match[1]}`);
    }
    
    return hashtags;
  };

  // Reset errors when form data changes
  useEffect(() => {
    setErrors({});
  }, [formData]);

  // Sync form language with app language
  useEffect(() => {
    const rawLanguage = (typeof i18n.language === 'string' ? i18n.language : 'en') as string;
    
    // Smart language matching: prefer exact match, fallback to language-only
    const availableLanguages = [
      'en', 'en-US', 'en-GB', 
      'fr', 'fr-CA', 
      'de', 'es', 'es-MX', 
      'it', 'pt', 'pt-BR', 
      'nl', 'ru', 'ar', 'zh', 'ja', 'ko', 'hi', 'bn', 'tr', 'vi', 'tl', 'fa', 'pl', 'th', 'sv', 'ro', 'uk', 'cs', 'lb', 'da', 'fi', 'no'
    ];
    
    // Convert to lowercase for case-insensitive matching
    const normalizedLanguage = rawLanguage.toLowerCase();
    let appLanguage = normalizedLanguage;
    
    // First, try exact match
    if (!availableLanguages.includes(normalizedLanguage)) {
      // If no exact match, try language-only (e.g., 'fr-FR' -> 'fr')
      const languageOnly = normalizedLanguage.split('-')[0];
      appLanguage = availableLanguages.includes(languageOnly) ? languageOnly : 'en';
    }
    
    if (appLanguage !== formData.language) {
      setFormData(prev => ({ ...prev, language: appLanguage }));
    }
  }, [i18n.language]);



  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.brandId) {
      newErrors.brandId = t('contentCreation.errors.brandRequired');
    }
    
    if (!formData.personaId) {
      newErrors.personaId = t('contentCreation.errors.personaRequired');
    }
    
    if (formData.platforms.length === 0) {
      newErrors.platforms = t('contentCreation.errors.platformsRequired');
    }
    
    // Make description optional if sourceUrl is provided
    if (!formData.shortDescription.trim() && !(formData.sourceUrl?.trim())) {
      newErrors.shortDescription = t('contentCreation.errors.descriptionOrSourceRequired');
    }
    
    if (!formData.tone) {
      newErrors.tone = t('contentCreation.errors.toneRequired');
    }
    
    if (!formData.contentLength) {
      newErrors.contentLength = t('contentCreation.errors.contentLengthRequired');
    }
    
    if (!formData.language) {
      newErrors.language = t('contentCreation.errors.languageRequired');
    }
    
    if (formData.shortDescription.length > 600) {
      newErrors.shortDescription = t('contentCreation.errors.descriptionTooLong', `Description too long (max 600 characters)`);
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Set the flag to allow content generation
    allowContentGeneration.current = true;
    
    if (validateForm()) {
      // IMPORTANT: This is the only place where content generation should be triggered
      // Content is only generated when the user explicitly clicks the Generate or Regenerate button
      
      // Create a copy of formData to modify before sending
      const requestData = { ...formData };
      
      // Use the value from React state, not direct DOM access
      requestData.shortDescription = formData.shortDescription;
      
      // If generating an image, add the style_type parameter in uppercase
      if (requestData.generateImage && requestData.imageStyle) {
        // Ensure RENDER_3D is properly formatted from '3d'
        const styleType = requestData.imageStyle === '3d' ? 'RENDER_3D' : requestData.imageStyle.toUpperCase();
        requestData.style_type = styleType;
        
        // Add aspect_ratio parameter based on imageSize
        if (requestData.imageSize) {
          const aspectRatioMap: Record<string, string> = {
            'square': '1x1',
            '9:16': '9x16',
            '4:5': '4x5'
          };
          requestData.aspect_ratio = aspectRatioMap[requestData.imageSize] || '1x1';  // Default to square if not found
          
          // Log the mapped aspect ratio for debugging
          console.log('Using aspect ratio:', requestData.aspect_ratio, 'from size:', requestData.imageSize);
        }
      }
      
      console.log('*** CONTENT GENERATION EXPLICITLY TRIGGERED BY USER ACTION ***');
      console.log('Request data:', requestData);
      
      // Only proceed if content generation is explicitly allowed
      if (allowContentGeneration.current) {
        console.log('Content generation allowed, making API call');
        contentGeneration.mutate(requestData, {
          onSuccess: (data) => {
            // Reset the flag after successful content generation
            allowContentGeneration.current = false;
            
            console.log('Content generation API call successful, raw response:', data);
            
            // Transform the response to the expected format
            const transformedData = transformContentResponse(data);
            console.log('Setting generated content with transformed data:', transformedData);
            setGeneratedContent(transformedData);
            setHasGeneratedNewContent(true);
          },
          onError: (error) => {
            // Reset the flag after error
            allowContentGeneration.current = false;
            
            console.error('Content generation API call failed:', error);
            setErrors({
              submit: error.message || t('contentCreation.errors.generationFailed'),
            });
          },
        });
      } else {
        console.log('Content generation attempted but not allowed - not explicitly triggered by user');
      }
    } else {
      console.warn('Form validation failed, not proceeding with content generation');
      console.log('Current errors:', errors);
      
      // Scroll to the form actions section to show the error summary
      setTimeout(() => {
        const formActionsElement = document.querySelector('form .flex.justify-between');
        if (formActionsElement) {
          formActionsElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Add useEffect for scrolling after content generation
  useEffect(() => {
    if (hasGeneratedNewContent && resultsRef.current) {
      // Ensure the element is still in the document before scrolling
      if (document.body.contains(resultsRef.current)) {
        if (window.innerWidth >= 1024) { // lg breakpoint
          scrollToTop();
        } else {
          resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      setHasGeneratedNewContent(false); // Reset the flag
    }
  }, [hasGeneratedNewContent, generatedContent, scrollToTop, scrollToResults]);

  // Update the "Regenerate" button click handler to ensure it explicitly calls handleSubmit
  const handleRegenerate = () => {
    // Set the flag to allow content generation
    allowContentGeneration.current = true;
    
    // Create a synthetic event object 
    const syntheticEvent = { preventDefault: () => {} };
    // Call the regular submit handler
    handleSubmit(syntheticEvent as React.FormEvent);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Track if shortDescription is being manually edited
    if (name === 'shortDescription') {
      manuallyEditedDescription.current = true;
      console.log('Description manually edited to:', value);
    }
    
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    // Reset the form data
    setFormData({
      brandId: '',
      personaId: '',
      platforms: [],
      topic: '',
      shortDescription: '',
      tone: 'professional',
      contentLength: 'medium',
      language: 'en',
      keywords: [],
      sourceUrl: '',
      format: 'regular',
      generateImage: false,
      imageStyle: 'auto',
      imageSize: '4:5',
    });
    
    // Reset the generated content
    setGeneratedContent(null);
    
    // Reset any errors
    setErrors({});
    
    // Reset the manually edited description flag
    manuallyEditedDescription.current = false;
    
    // Reset the auto-selection flag to allow a new auto-selection after reset
    hasAutoSelectedIdea.current = false;
    
    // Reset keywords input state
    setKeywordsInputValue('');
    
    console.log('Form reset, including manually edited description flag and auto-selection flag');
  };

  const handleCopyAll = () => {
    if (generatedContent?.data?.contents) {
      const allContent = generatedContent.data.contents
        .map((item) => {
          return `${item.platform.toUpperCase()}\n\n${item.content}${
            item.hashtags && item.hashtags.length > 0 ? '\n\n' + item.hashtags.join(' ') : ''
          }\n\n---\n\n`;
        })
        .join('');
      
      navigator.clipboard.writeText(allContent);
      setAllCopied(true);
      setTimeout(() => setAllCopied(false), 2000);
    }
  };

  const handleCreatePost = (platform: string, content: string, hashtags?: string[]) => {
    const platformContent = generatedContent?.data?.contents?.find(item => item.platform === platform);
    const imageUrl: string | null = typeof platformContent === 'object' && platformContent 
      ? ('imageUrl' in platformContent && typeof platformContent.imageUrl === 'string' ? platformContent.imageUrl : null) 
      : null;
      
    const normalizedPlatform = platform.toLowerCase() as SocialPlatform;
    
    // Check if there are other posts
    const otherPosts = generatedContent?.data?.contents?.filter(item => item.platform.toLowerCase() !== normalizedPlatform) || [];

    if (otherPosts.length > 0) {
      setSelectedPostForEditing({ platform: normalizedPlatform, content, hashtags, imageUrl });
      setIsAIPostDraftModalOpen(true);
    } else {
      // No other posts, proceed directly to editing
    navigate('/posts/new', { 
      state: { 
        content, 
        platform: normalizedPlatform, 
        hashtags,
        brandId: formData.brandId,
        imageUrl: imageUrl
      } 
    });
    }
  };

  // Handle "Discard" action from AIPostDraftModal
  const handleDiscardOtherPosts = () => {
    setIsAIPostDraftModalOpen(false);
    if (selectedPostForEditing) {
      navigate('/posts/new', { 
        state: { 
          content: selectedPostForEditing.content, 
          platform: selectedPostForEditing.platform as SocialPlatform, 
          hashtags: selectedPostForEditing.hashtags,
          brandId: formData.brandId,
          imageUrl: selectedPostForEditing.imageUrl
        } 
      });
      setSelectedPostForEditing(null);
    }
  };

  // Handle "Save as drafts" action from AIPostDraftModal
  const handleSaveOtherPostsAndProceed = async () => {
    if (!generatedContent?.data?.contents || !selectedPostForEditing) return;

    setIsSavingDrafts(true);
    const postsToSave = generatedContent.data.contents.filter(
      item => item.platform.toLowerCase() !== selectedPostForEditing?.platform.toLowerCase()
    );

    try {
      for (const post of postsToSave) {
        const postPlatformContent: any = generatedContent?.data?.contents?.find(p => p.platform === post.platform);
        
        let postImageUrl: string | null = null;
        if (postPlatformContent && 
            typeof postPlatformContent === 'object' && 
            postPlatformContent !== null && // Ensure it's not null
            postPlatformContent.hasOwnProperty('imageUrl')) {
          if (typeof postPlatformContent.imageUrl === 'string') {
            postImageUrl = postPlatformContent.imageUrl;
          } else if (postPlatformContent.imageUrl === null) {
            postImageUrl = null;
          }
        }

        let contentText = '';
        if (typeof post.content === 'string') {
          contentText = post.content;
        } else if (post.content && typeof post.content === 'object' && post.content !== null && post.content.hasOwnProperty('text')) {
          const contentAsAny = post.content as any;
          if (typeof contentAsAny.text === 'string') {
            contentText = contentAsAny.text;
          }
        }

        const draftPayload: any = {
          brandId: formData.brandId,
          title: '',
          content: contentText,
          format: formData.format || 'regular',
          platforms: [post.platform.toLowerCase() as SocialPlatform],
          needsReview: false,
          reviewerId: '',
          tags: [],
          hashtags: post.hashtags || [],
          platformSettings: {}
        };

        if (postImageUrl) {
          draftPayload.media = {
            url: postImageUrl,
            type: 'image' as const, // Assuming image, adjust if video can be generated
            thumbnailUrl: ''
          };
        }

        await saveDraft(draftPayload);
      }
      // After saving, proceed to edit the selected post
      handleDiscardOtherPosts(); // This will navigate
    } catch (error) {
      console.error('Error saving other posts as drafts:', error);
      // Optionally show a toast message for the error
      // For simplicity, we'll still proceed to edit the selected post even if some drafts fail to save.
      handleDiscardOtherPosts();
    } finally {
      setIsSavingDrafts(false);
      setIsAIPostDraftModalOpen(false);
    }
  };

  // Handle saving a draft post with image
  const handleSaveDraft = async (platform: string, content: string, hashtags?: string[], imageUrl?: string | null) => {
    console.log(`Saving draft for ${platform} with content and image`);
    
    // Vérifier si un brand est sélectionné
    if (!formData.brandId) {
      setErrors({
        ...errors,
        submit: 'Veuillez sélectionner une marque avant de sauvegarder'
      });
      return;
    }
    
    try {
      // Préparer le payload pour le service de brouillons
      const draftPayload = {
        brandId: formData.brandId,
        title: '',
        content,
        format: formData.format || 'regular', // Utiliser 'regular' comme valeur par défaut
        platforms: [platform], // Utiliser spécifiquement cette plateforme
        needsReview: false,
        reviewerId: '',
        tags: [],
        hashtags: hashtags || [],
        platformSettings: {},
        // Ajouter les informations d'image si disponibles
        ...(imageUrl && {
          media: {
            url: imageUrl,
            type: 'image' as const,
            thumbnailUrl: ''
          }
        })
      };
      
      // Utiliser le service pour sauvegarder le brouillon
      await saveDraft(draftPayload);
      
      // Réinitialiser les erreurs
      setErrors({});
      
      console.log('Draft saved successfully');
    } catch (error) {
      console.error('Error saving draft:', error);
      
      // Afficher l'erreur à l'utilisateur
      setErrors({
        ...errors,
        submit: `Erreur lors de la sauvegarde: ${error instanceof Error ? error.message : String(error)}`
      });
    }
  };

  // Handle idea selection
  const handleIdeaSelect = (idea: any) => {
    // Track that we're selecting a content idea to avoid overwriting manual edits
    console.log('Content idea selected:', idea);
    
    // Reset the manually edited flag since we're explicitly choosing a content idea
    manuallyEditedDescription.current = false;
    
    let newTopic = '';
    let newDescription = '';
    
    // Extract the title and description based on the format of the idea
    if (typeof idea === 'object' && idea !== null) {
      newTopic = idea.title || '';
      newDescription = idea.description || '';
    } else {
      // If it's just a string, treat it as the description
      newDescription = idea;
    }
    
    // Update the form data state
    setFormData(prev => ({
      ...prev,
      topic: newTopic,
      shortDescription: newDescription
    }));
    
    // No need to directly manipulate the DOM anymore
    console.log('Updated description via state with:', newDescription);
  };

  // Handle purging description and deselecting content idea
  const handlePurgeDescription = () => {
    console.log('Purging description and deselecting content idea');
    
    // Clear the description and topic
    setFormData(prev => ({
      ...prev,
      topic: '',
      shortDescription: ''
    }));
    
    // Reset the manually edited flag
    manuallyEditedDescription.current = false;
  };

  // Handle keywords input
  const handleKeywordsInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && keywordsInputValue.trim()) {
      e.preventDefault();
      const newKeyword = keywordsInputValue.trim();
      
      // Add the keyword if it's not already in the list
      if (!formData.keywords.includes(newKeyword)) {
        setFormData(prev => ({
          ...prev,
          keywords: [...prev.keywords, newKeyword]
        }));
      }
      
      // Clear the input
      setKeywordsInputValue('');
    }
  };

  // Card style with black stroke
  const cardStyle = "bg-white rounded-lg shadow-md p-6 border border-black";
  const cardHeaderStyle = "flex items-center gap-2 mb-4 pb-2 border-b border-gray-200";
  const cardTitleStyle = "text-lg font-semibold text-gray-800";

  // Add a function to handle generating more ideas
  const handleGenerateMoreIdeas = async () => {
    if (isGeneratingMoreIdeas) return;
    
    console.log('🎲 Manually generating more ideas...');
    try {
      await generateMoreIdeas();
      console.log('✅ More ideas generated successfully');
    } catch (error) {
      console.error('❌ Failed to generate more ideas:', error);
    }
  };

  // Handle image regeneration
  const handleImageRegenerate = (imageStyle: string, imageSize: ImageSize) => {
    if (!generatedContent?.data?.contents || !formData.brandId || !formData.personaId) {
      console.error('Missing required data for image regeneration');
      return;
    }

    // Get the first post content for image generation
    const firstPost = generatedContent.data.contents[0];
    let postContent = '';
    
    if (typeof firstPost.content === 'string') {
      postContent = firstPost.content;
    } else if (firstPost.content && typeof firstPost.content === 'object' && 'text' in firstPost.content) {
      postContent = (firstPost.content as any).text || '';
    }

    if (!postContent) {
      console.error('No post content found for image regeneration');
      return;
    }

    const request = {
      postContent,
      brandId: formData.brandId,
      personaId: formData.personaId,
      aspect_ratio: convertImageSizeToAspectRatio(imageSize),
      style_type: convertImageStyleToStyleType(imageStyle)
    };

    imageRegeneration.mutate(request, {
      onSuccess: (response) => {
        // Store the generated image temporarily for validation
        if (response.imageUrl) {
          setTemporaryGeneratedImageUrl(response.imageUrl);
        }
      },
      onError: (error) => {
        console.error('Image regeneration failed:', error);
        setErrors({
          ...errors,
          submit: error.message || 'Image regeneration failed'
        });
      }
    });
  };

  // Handle image validation (user confirms they want to use the new image)
  const handleImageValidation = (imageUrl: string) => {
    if (generatedContent) {
      // Update both the main imageUrl and all platform-specific imageUrls
      const updatedContent = {
        ...generatedContent,
        imageUrl: imageUrl,
        data: {
          ...generatedContent.data,
          contents: generatedContent.data?.contents?.map(content => ({
            ...content,
            imageUrl: imageUrl
          })) || []
        }
      };
      
      setGeneratedContent(updatedContent);
      console.log('✅ Image updated for all platforms:', imageUrl);
    }
    
    // Reset temporary state and close modal
    setTemporaryGeneratedImageUrl(null);
    setIsImageRegenerationModalOpen(false);
  };

  // Example: Add cleanup to effects that process content or set state after async work
  useEffect(() => {
    let isMounted = true;
    // Example async effect (replace with actual async logic if present)
    const processContent = async () => {
      // ... your async logic ...
      if (isMounted) {
        // setProcessedContent(result); // Example state update
      }
    };
    // if (content) processContent(); // Uncomment and adapt as needed
    return () => {
      isMounted = false;
    };
  }, [/* dependencies for async effect, e.g. content, language */]);



  return (
    <>
      <PageLayout
        title={t('contentCreation.title')}
        subtitle={t('contentCreation.subtitle')}
      >

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Form Section */}
            <div>
              <form 
                onSubmit={(e) => {
                  console.log('Form submit event triggered');
                  e.preventDefault();
                  handleSubmit(e);
                }} 
                className="space-y-6"
              >
                {/* Card 1: Audience */}
                <div className={cardStyle}>
                  <div className={cardHeaderStyle}>
                    <Users className="h-5 w-5 text-primary-600" />
                    <h2 className={cardTitleStyle}>Audience</h2>
                  </div>
                  <div className="space-y-6">
                    {/* Brand Selection */}
                    <div>
                      <label htmlFor="brandId" className="block text-sm font-medium text-gray-700 mb-3">
                        {t('contentCreation.form.brand')} <span className="text-red-500">*</span>
                      </label>
                      
                      {/* Brand Grid Selection */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {brands.map((brand: any) => (
                          <button
                            key={brand.id}
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, brandId: brand.id }))}
                            className={`flex flex-col items-center gap-2 p-3 rounded-lg border border-black ${
                              formData.brandId === brand.id
                                ? 'border-primary-600 bg-primary-50'
                                : 'hover:border-primary-600'
                            }`}
                          >
                            <img 
                              src={brand.logo} 
                              alt={brand.name}
                              className="w-10 h-10 rounded-lg object-cover"
                              onError={(e) => {
                                // Handle image loading errors
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                            <span className="text-sm text-gray-700 truncate max-w-full">{brand.name}</span>
                          </button>
                        ))}
                      </div>
                      {errors.brandId && <p className="mt-1 text-sm text-red-600">{errors.brandId}</p>}
                    </div>

                    {/* Persona Selection */}
                    <div>
                      <label htmlFor="personaId" className="block text-sm font-medium text-gray-700 mb-3">
                        {t('contentCreation.form.persona')} <span className="text-red-500">*</span>
                      </label>
                      
                      {/* Persona Grid Selection */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {isLoadingPersonas ? (
                          // Loading state - show skeleton loaders
                          Array(2).fill(0).map((_, index) => (
                            <div 
                              key={index}
                              className="flex items-start p-3 rounded-lg border border-gray-200 animate-pulse"
                            >
                              <div className="w-12 h-12 rounded-lg bg-gray-200 flex-shrink-0"></div>
                              <div className="ml-3 text-left w-full">
                                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                              </div>
                            </div>
                          ))
                        ) : personas.length > 0 ? (
                          // Show personas grid if we have personas
                          personas.map((persona: any) => {
                            // Function to get language emoji
                            const getLanguageEmoji = (lang: string) => {
                              switch (lang.toLowerCase()) {
                                case 'en':
                                  return '🇬🇧';
                                case 'fr':
                                  return '🇫🇷';
                                case 'es':
                                  return '🇪🇸';
                                default:
                                  return '🌐';
                              }
                            };
                            
                            return (
                              <button
                                key={persona.id}
                                type="button"
                                onClick={() => setFormData((prev) => ({ ...prev, personaId: persona.id }))}
                                className={`flex items-start p-3 rounded-lg border border-black ${
                                  formData.personaId === persona.id
                                    ? 'border-primary-600 bg-primary-50'
                                    : 'hover:border-primary-600'
                                }`}
                              >
                                {/* Left column - Avatar */}
                                {persona.avatarUrl ? (
                                  <img 
                                    src={persona.avatarUrl} 
                                    alt={persona.name}
                                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                    onError={(e) => {
                                      // Handle image loading errors
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-lg border border-gray-200 bg-primary-50 flex items-center justify-center flex-shrink-0">
                                    <User className="w-6 h-6 text-primary-600" />
                                  </div>
                                )}
                                
                                {/* Right column - Details */}
                                <div className="ml-3 text-left">
                                  <span className="text-sm font-medium text-gray-800 block">
                                    {persona.name}
                                  </span>
                                  <span className="text-xs text-gray-500 block">
                                    {persona.jobTitle}
                                  </span>
                                  <div className="flex items-center gap-1 mt-1">
                                    <span className="text-xs text-gray-500">{persona.age}</span>
                                    {persona.languages && persona.languages.map((lang: string, index: number) => (
                                      <span key={index} className="text-xs">
                                        {getLanguageEmoji(lang)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </button>
                            );
                          })
                        ) : (
                          // Show empty state if no personas
                          <div className="col-span-2 p-4 border border-dashed border-gray-300 rounded-lg text-center">
                            <p className="text-sm text-gray-500 mb-3">
                              {t('contentCreation.form.noPersonas', 'No personas available for this brand')}
                            </p>
                            <Link 
                              to="/ai-personas-builder" 
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                              {t('contentCreation.form.createPersona', 'Create a Persona')}
                            </Link>
                          </div>
                        )}
                      </div>
                      {errors.personaId && <p className="mt-1 text-sm text-red-600">{errors.personaId}</p>}
                      
                      {/* Only show the dropdown if explicitly needed for accessibility */}
                      <div className="sr-only">
                        <select
                          id="personaId"
                          name="personaId"
                          value={formData.personaId}
                          onChange={handleInputChange}
                          aria-hidden="true"
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                        >
                          <option value="">{t('contentCreation.form.selectPersona')}</option>
                          {personas.map((persona: any) => (
                            <option key={persona.id} value={persona.id}>
                              {persona.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 2: Platforms */}
                <div className={cardStyle}>
                  <div className={cardHeaderStyle}>
                    <Share2 className="h-5 w-5 text-primary-600" />
                    <h2 className={cardTitleStyle}>{t('cards.platforms.title', 'Platforms')}</h2>
                  </div>
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {t('contentCreation.form.platforms')} <span className="text-red-500">*</span>
                    </label>
                    
                    {/* Platforms Grid Selection */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {socialPlatforms.map(platform => {
                        if (platform.id === 'all') return null;
                        
                        const platformId = platform.id as string;
                        const isSelected = formData.platforms.includes(platformId);
                        const connectedAccount = connectedAccounts.find((acc: any) => acc.platform === platformId);
                        
                        return (
                          <button
                            key={platform.id}
                            type="button"
                            onClick={() => {
                              if (!formData.platforms.includes(platformId)) {
                                setFormData((prev) => ({ 
                                  ...prev, 
                                  platforms: [...prev.platforms, platformId] 
                                }));
                              } else {
                                setFormData((prev) => ({
                                  ...prev,
                                  platforms: prev.platforms.filter(p => p !== platformId)
                                }));
                              }
                            }}
                            className={`flex flex-col items-center gap-2 p-3 rounded-lg border border-black ${
                              isSelected
                                ? 'border-primary-600 bg-primary-50'
                                : 'hover:border-primary-600'
                            }`}
                          >
                            {connectedAccount ? (
                              <>
                                <SocialProfilePicture
                                  pictureUrl={connectedAccount.picture_url}
                                  username={connectedAccount.username}
                                  platform={platform.id as SocialPlatform}
                                  size="md"
                                />
                                <span className="text-xs text-gray-700 text-center line-clamp-2 overflow-hidden whitespace-normal break-words">
                                  @{connectedAccount.username}
                                </span>
                              </>
                            ) : (
                              <>
                                <SocialIcon platform={platform.id as SocialPlatform} size={24} />
                                <span className="text-sm text-gray-700">{platform.name}</span>
                              </>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {errors.platforms && <p className="mt-1 text-sm text-red-600">{errors.platforms}</p>}
                    
                    {/* Format Selection */}
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        {t('contentCreation.form.format', 'Content Format')}
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, format: 'regular' }))}
                          className={`flex items-center gap-3 p-3 rounded-lg border border-black ${
                            formData.format === 'regular'
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'hover:border-primary-600'
                          }`}
                        >
                          <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full">
                            <span className="text-xl">📸</span>
                          </div>
                          <div className="text-left">
                            <div className="font-medium text-sm">{t('postContent.regularFormat', 'Classic (Horizontal)')}</div>
                            <div className="text-xs text-gray-500">{t('contentCreation.formats.regularDescription', 'Standard posts for your feed')}</div>
                          </div>
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, format: 'reel' }))}
                          className={`flex items-center gap-3 p-3 rounded-lg border border-black ${
                            formData.format === 'reel'
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'hover:border-primary-600'
                          }`}
                        >
                          <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full">
                            <span className="text-xl">📱</span>
                          </div>
                          <div className="text-left">
                            <div className="font-medium text-sm">{t('postContent.reelFormat', 'Reel (Vertical)')}</div>
                            <div className="text-xs text-gray-500">{t('contentCreation.formats.reelDescription', 'Short-form video content')}</div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 3: Content */}
                <div className={cardStyle}>
                  <div className={cardHeaderStyle}>
                    <FileText className="h-5 w-5 text-primary-600" />
                    <h2 className={cardTitleStyle}>{t('contentCreation.form.contentCard', 'Content')}</h2>
                  </div>
                  <div className="space-y-6">
                    {/* Content Ideas */}
                    {(formData.brandId && formData.personaId) && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {t('contentCreation.form.contentIdeas', 'Content Ideas')}
                          </label>
                          <button
                            onClick={handleGenerateMoreIdeas}
                            disabled={isIdeasLoading || isGeneratingMoreIdeas || !formData.brandId || !formData.personaId}
                                                          className={`inline-flex items-center px-3 py-1.5 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed ${
                                isIdeasLoading || isGeneratingMoreIdeas ? 'bg-gray-400 hover:bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'
                            }`}
                          >
                            {isIdeasLoading || isGeneratingMoreIdeas ? (
                              <>
                                <div className="w-4 h-4 mr-1.5 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                                <span className="text-sm font-semibold">{t('common.generating', 'Generating...')}</span>
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-4 h-4 mr-1.5" />
                                <span className="text-sm font-semibold">{t('contentCreation.form.generateMoreIdeas', 'Generate 5 more ideas')}</span>
                              </>
                            )}
                          </button>
                        </div>
                        
                        {(isIdeasLoading) ? (
                          <div className="grid grid-cols-1 gap-2">
                            {Array(5).fill(0).map((_, index) => (
                              <div key={index} className="flex items-center w-full px-3 py-2 rounded-md text-sm border border-gray-200 animate-pulse">
                                <div className="h-3 w-3 bg-gray-300 rounded-sm mr-2 flex-shrink-0"></div>
                                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                              </div>
                            ))}
                          </div>
                        ) : hasIdeasError ? (
                          <div>
                            <p className="text-sm text-red-500 mb-2">
                              {ideasErrorMessage ? `Error: ${ideasErrorMessage}` : t('contentCreation.form.ideasError', 'Failed to load content ideas')}
                            </p>

                            <button
                              type="button"
                              onClick={() => refetchIdeas()}
                              className="text-xs text-primary-600 hover:text-primary-800"
                            >
                              {t('common.retry', 'Retry')}
                            </button>
                          </div>
                        ) : contentIdeas.length > 0 ? (
                          <div className="grid grid-cols-1 gap-2">
                            {contentIdeas.map((idea: any, index: number) => {
                              // Handle both string and object formats
                              const title = typeof idea === 'object' && idea !== null ? idea.title : idea;
                              
                              // Check if this idea is currently selected
                              const isSelected = 
                                (typeof idea === 'object' && idea !== null && 
                                 idea.title === formData.topic && 
                                 idea.description === formData.shortDescription) ||
                                (typeof idea === 'string' && idea === formData.shortDescription);
                              
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => handleIdeaSelect(idea)}
                                  className={`flex items-center w-full px-3 py-2 rounded-md text-sm border transition-colors ${
                                    isSelected 
                                      ? 'bg-primary-50 text-primary-700 border-primary-200 hover:bg-primary-100' 
                                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <Tag className={`h-3 w-3 min-w-3 mr-2 flex-shrink-0 ${
                                    isSelected ? 'text-primary-600' : 'text-gray-400'
                                  }`} />
                                  <span 
                                    className="text-left truncate w-full" 
                                    style={{ textTransform: 'none' }} // Ensure no text transformation
                                  >
                                    {title}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">
                            {t('contentCreation.form.noIdeas', 'No content ideas available')}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Source URL */}
                    <div>
                      <label htmlFor="sourceUrl" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('contentCreation.form.sourceUrl')}
                      </label>
                      <input
                        type="url"
                        id="sourceUrl"
                        name="sourceUrl"
                        value={formData.sourceUrl}
                        onChange={handleInputChange}
                        placeholder={t('contentCreation.form.sourceUrlPlaceholder', 'Enter source URL (optional)')}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700">
                          {t('contentCreation.form.description')}
                        </label>
                        <span className={`text-xs ${formData.shortDescription.length > 600 ? 'text-red-600' : 'text-gray-500'}`}>
                          {formData.shortDescription.length}/600 {t('common.characters', 'characters')}
                        </span>
                      </div>
                      <MemoizedDescriptionTextarea 
                        value={formData.shortDescription}
                        onChange={(e) => {
                          manuallyEditedDescription.current = true;
                          handleInputChange(e);
                        }}
                        placeholder={t('contentCreation.form.descriptionPlaceholder')}
                      />
                      <div className="flex justify-between items-center mt-1">
                        <div className="flex-1">
                          {errors.shortDescription && (
                            <p className="text-sm text-red-600">{errors.shortDescription}</p>
                          )}
                          {manuallyEditedDescription.current && !errors.shortDescription && (
                            <p className="text-xs text-blue-600">
                              {t('contentCreation.form.manuallyEdited', 'You have manually edited this description')}
                            </p>
                          )}
                        </div>
                        {(formData.shortDescription || formData.topic) && (
                          <button
                            type="button"
                            onClick={handlePurgeDescription}
                            className="text-sm text-primary-600 hover:text-primary-800 font-medium transition-colors ml-4"
                          >
                            {t('common.purge')}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Keywords */}
                    <div>
                      <label htmlFor="keywords" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('contentCreation.form.keywords')}
                      </label>
                      <TagInput
                        tags={formData.keywords}
                        onTagsChange={(keywords) => setFormData((prev) => ({ ...prev, keywords }))}
                        placeholder={t('contentCreation.form.keywordsPlaceholder')}
                        inputValue={keywordsInputValue}
                        onInputChange={setKeywordsInputValue}
                        onInputKeyDown={handleKeywordsInputKeyDown}
                      />
                    </div>

                    {/* Content Length */}
                    <div>
                      <label htmlFor="contentLength" className="block text-sm font-medium text-gray-700 mb-3">
                        {t('contentCreation.form.contentLength')} <span className="text-red-500">*</span>
                      </label>
                      
                      {/* Content Length Grid Selection */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                          { 
                            value: 'short' as 'short', 
                            label: t('contentCreation.contentLength.short', 'Short'), 
                            emoji: '📱', 
                            description: t('contentCreation.contentLength.shortDescription', 'Brief content, ideal for quick engagement')
                          },
                          { 
                            value: 'medium' as 'medium', 
                            label: t('contentCreation.contentLength.medium', 'Medium'), 
                            emoji: '📄', 
                            description: t('contentCreation.contentLength.mediumDescription', 'Balanced length for most platforms')
                          },
                          { 
                            value: 'long' as 'long', 
                            label: t('contentCreation.contentLength.long', 'Long'), 
                            emoji: '📚', 
                            description: t('contentCreation.contentLength.longDescription', 'In-depth content with detailed information')
                          }
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, contentLength: option.value }))}
                            className={`flex items-center gap-3 p-3 rounded-lg border ${
                              formData.contentLength === option.value
                                ? 'border-primary-600 bg-primary-50 text-primary-700'
                                : 'border-gray-300 hover:border-primary-600'
                            }`}
                          >
                            <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full">
                              <span className="text-xl">{option.emoji}</span>
                            </div>
                            <div className="text-left">
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-gray-500">{option.description}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                      {errors.contentLength && <p className="mt-1 text-sm text-red-600">{errors.contentLength}</p>}
                    </div>
                  </div>
                </div>

                {/* Card 4: Voice */}
                <div className={cardStyle}>
                  <div className={cardHeaderStyle}>
                    <MessageSquare className="h-5 w-5 text-primary-600" />
                    <h2 className={cardTitleStyle}>{t('contentCreation.form.voice', 'Voice')}</h2>
                  </div>
                  <div className="space-y-6">
                    {/* Tone */}
                    <div>
                      <label htmlFor="tone" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('contentCreation.form.tone')} <span className="text-red-500">*</span>
                      </label>
                      <MemoizedContentToneSelect
                        value={formData.tone}
                        onChange={(tone) => {
                          console.log('ContentToneSelect onChange triggered with value:', tone);
                          setFormData((prev) => ({ ...prev, tone }));
                          // Explicitly log that we don't want to generate content here
                          console.log('Form data updated for tone, NO CONTENT GENERATION should be triggered');
                        }}
                      />
                      {errors.tone && <p className="mt-1 text-sm text-red-600">{errors.tone}</p>}
                    </div>

                    {/* Language */}
                    <div>
                      <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                        {t('contentCreation.form.language')} <span className="text-red-500">*</span>
                      </label>
                      <LanguageDropdown
                        value={formData.language || 'en'}
                        onChange={(language) => {
                          console.log('Language changed to:', language);
                          setFormData((prev) => ({ ...prev, language }));
                        }}
                        buttonLabel={t('contentCreation.form.selectLanguage')}
                      />
                      {errors.language && <p className="mt-1 text-sm text-red-600">{errors.language}</p>}
                    </div>
                  </div>
                </div>

                {/* Card 5: Post Image */}
                <div className={cardStyle}>
                  <div className={cardHeaderStyle}>
                    <Image className="h-5 w-5 text-primary-600" />
                    <h2 className={cardTitleStyle}>{t('contentCreation.form.postImage', 'Post Image')}</h2>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="imageSelection" className="block text-sm font-medium text-gray-700 mb-3">
                        {t('contentCreation.form.imageSelection', 'Image Selection')}
                      </label>
                      
                      {/* Image Selection Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          { 
                            value: false, 
                            label: t('contentCreation.form.textOnly', 'Text Only'), 
                            emoji: '📝', 
                            description: t('contentCreation.form.textOnlyDescription', 'Generate content without an image')
                          },
                          { 
                            value: true, 
                            label: t('contentCreation.form.withImage', 'With an Image'), 
                            emoji: '🖼️', 
                            description: t('contentCreation.form.withImageDescription', 'Generate content with a related image')
                          }
                        ].map((option) => (
                          <button
                            key={String(option.value)}
                            type="button"
                            onClick={() => {
                              // Only update the formData state, don't trigger content generation
                              setFormData(prev => ({ ...prev, generateImage: option.value }));
                            }}
                            className={`flex items-center gap-3 p-3 rounded-lg border ${
                              formData.generateImage === option.value
                                ? 'border-primary-600 bg-primary-50 text-primary-700'
                                : 'border-gray-300 hover:border-primary-600'
                            }`}
                          >
                            <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full">
                              <span className="text-xl">{option.emoji}</span>
                            </div>
                            <div className="text-left">
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-gray-500">{option.description}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Show Image Style selection only when "With an Image" is selected */}
                    {formData.generateImage && (
                      <div className="mt-4">
                        <label htmlFor="imageStyle" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('contentCreation.form.imageStyle')}
                        </label>
                        <MemoizedImageStyleSelect 
                          value={formData.imageStyle || 'auto'}
                          onChange={(imageStyle) => {
                            console.log('ImageStyleSelect onChange triggered with value:', imageStyle);
                            // Only update the formData state, don't trigger content generation
                            setFormData(prev => ({ ...prev, imageStyle }));
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Show Image Size selection only when "With an Image" is selected */}
                    {formData.generateImage && (
                      <div className="mt-4">
                        <label htmlFor="imageSize" className="block text-sm font-medium text-gray-700 mb-1">
                          {t('contentCreation.form.imageSize', 'Image Size')}
                        </label>
                        <MemoizedImageSizeSelect 
                          value={formData.imageSize as any || '4:5'}
                          onChange={(imageSize) => {
                            console.log('ImageSizeSelect onChange triggered with value:', imageSize);
                            // Only update the formData state, don't trigger content generation
                            setFormData(prev => ({ ...prev, imageSize }));
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit Error */}
                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <p className="text-sm text-red-600">{errors.submit}</p>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    {t('contentCreation.form.reset')}
                  </button>
                  <button
                    type="submit"
                    disabled={contentGeneration.isPending || formData.shortDescription.length > 600}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {contentGeneration.isPending ? (
                      <>
                        <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                        {t('contentCreation.form.generating')}
                      </>
                    ) : (
                      t('contentCreation.form.generate')
                    )}
                  </button>
                </div>
                
                {/* Validation Errors Summary */}
                {Object.keys(errors).length > 0 && !errors.submit && (
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                    <h3 className="text-sm font-medium text-red-800 mb-2">
                      {t('contentCreation.form.errorSummary', 'Please fix the following errors:')}
                    </h3>
                    <ul className="list-disc pl-5 space-y-1">
                      {Object.entries(errors).map(([field, message]) => (
                        <li key={field} className="text-sm text-red-600">
                          {message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </form>
            </div>

            {/* Results Section */}
            <div ref={resultsRef}>
              <div className="bg-white rounded-lg shadow-md p-6 border border-black h-full">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-800">
                    {t('contentCreation.results.title')}
                  </h2>
                  {generatedContent?.data?.contents && generatedContent.data.contents.length > 0 && (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleCopyAll}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        title={t('contentCreation.results.copyAll')}
                      >
                        {allCopied ? (
                          <>
                            <Check className="h-4 w-4 mr-1.5" />
                            {t('contentCreation.results.copied')}
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1.5" />
                            {t('contentCreation.results.copyAll')}
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleRegenerate}
                        disabled={contentGeneration.isPending}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t('contentCreation.results.regenerate')}
                      >
                        <RefreshCw className="h-4 w-4 mr-1.5" />
                        {t('contentCreation.results.regenerate')}
                      </button>
                    </div>
                  )}
                </div>

                {!contentGeneration.isPending && (
                  <>
                    {generatedContent?.data?.contents && generatedContent.data.contents.length > 0 ? (
                      <div className="space-y-6">
                        {/* Display generated image if available */}
                        {generatedContent.imageUrl && formData.generateImage && (
                          <div className="mb-6">
                            <div className="flex justify-between items-center mb-2">
                              <h3 className="text-md font-medium text-gray-700">{t('contentCreation.results.generatedImage', 'Generated Image')}</h3>
                              <button
                                onClick={() => setIsImageRegenerationModalOpen(true)}
                                disabled={imageRegeneration.isPending}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Sparkles className="h-4 w-4 mr-1.5" />
                                {t('contentCreation.results.regenerateImage', 'Regenerate the image')}
                              </button>
                            </div>
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              <img 
                                src={generatedContent.imageUrl} 
                                alt={t('contentCreation.results.generatedImageAlt', 'AI generated image for content')} 
                                className="w-full h-auto object-contain"
                              />
                            </div>
                          </div>
                        )}
                        {generatedContent.data.contents.map((item, index) => (
                          <ContentResultCard
                            key={item.platform || index}
                            platform={item.platform}
                            content={item.content}
                            hashtags={item.hashtags}
                            onCreatePost={(platform, content, hashtags) => handleCreatePost(platform, content, hashtags)}
                            brandId={formData.brandId}
                            format={formData.format}
                            imageUrl={generatedContent.imageUrl}
                            onSaveDraft={(platform, content, hashtags, imageUrl) => handleSaveDraft(platform, content, hashtags, imageUrl)}
                          />
                        ))}
                      </div>
                    ) : generatedContent?.content?.platforms ? (
                      // Fallback for direct rendering of new format if transformation didn't work
                      <div className="space-y-6">
                        {/* Display generated image if available */}
                        {generatedContent.imageUrl && formData.generateImage && (
                          <div className="mb-6">
                            <div className="flex justify-between items-center mb-2">
                              <h3 className="text-md font-medium text-gray-700">{t('contentCreation.results.generatedImage', 'Generated Image')}</h3>
                              <button
                                onClick={() => setIsImageRegenerationModalOpen(true)}
                                disabled={imageRegeneration.isPending}
                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Sparkles className="h-4 w-4 mr-1.5" />
                                {t('contentCreation.results.regenerateImage', 'Regenerate the image')}
                              </button>
                            </div>
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              <img 
                                src={generatedContent.imageUrl} 
                                alt={t('contentCreation.results.generatedImageAlt', 'AI generated image for content')} 
                                className="w-full h-auto object-contain"
                              />
                            </div>
                          </div>
                        )}
                        {Object.entries(generatedContent.content.platforms).map(([platform, content], index) => (
                          <ContentResultCard
                            key={platform}
                            platform={platform.toLowerCase()}
                            content={content}
                            hashtags={extractHashtags(typeof content === 'string' ? content : content.text || '')}
                            onCreatePost={(platform, content, hashtags) => handleCreatePost(platform, content, hashtags)}
                            brandId={formData.brandId}
                            format={formData.format}
                            imageUrl={generatedContent.imageUrl}
                            onSaveDraft={(platform, content, hashtags, imageUrl) => handleSaveDraft(platform, content, hashtags, imageUrl)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-64 text-center">
                        <p className="text-gray-500 max-w-md">
                          {t('contentCreation.results.noContent')}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
    </PageLayout>

      {/* Content Generation Loader */}
      <ContentGenerationLoader 
        isVisible={contentGeneration.isPending} 
        hasImage={Boolean(formData.generateImage)}
      />

      {/* AI Post Draft Modal */}
      {generatedContent?.data?.contents && selectedPostForEditing && (
        <AIPostDraftModal
          isOpen={isAIPostDraftModalOpen}
          onClose={() => {
            setIsAIPostDraftModalOpen(false);
            setSelectedPostForEditing(null);
          }}
          onDiscard={handleDiscardOtherPosts}
          onSaveAndProceed={handleSaveOtherPostsAndProceed}
          totalPosts={generatedContent.data.contents.length}
          isLoadingSave={isSavingDrafts}
        />
      )}

      {/* Waiting Modal for saving drafts */}
      <WaitingModal
        isOpen={isSavingDrafts}
        title={t('contentCreation.modals.savingDrafts.title', 'Saving Drafts')}
        message={t('contentCreation.modals.savingDrafts.message', 'Please wait while we save the other generated posts as drafts...')}
        gifUrl="https://media.socialness.ai/loading/unicorn.gif"
      />

      {/* Image Regeneration Modal */}
      <AISimpleGenerationModal
        isOpen={isImageRegenerationModalOpen}
        onClose={() => {
          setIsImageRegenerationModalOpen(false);
          setTemporaryGeneratedImageUrl(null);
        }}
        onGenerate={handleImageRegenerate}
        onValidate={handleImageValidation}
        isLoading={imageRegeneration.isPending}
        currentImageStyle={formData.imageStyle || 'auto'}
        currentImageSize={(formData.imageSize as ImageSize) || '4:5'}
        generatedImageUrl={temporaryGeneratedImageUrl}
      />
    </>
  );
};

export default ContentCreation; 